import { useMemo, useState } from "react";

import { Box, Stack, Typography, useTheme } from "@mui/material";
import { type EChartsOption } from "echarts";
import ECharts from "echarts-for-react";

import { useWorkloadsMetrics } from "../../../hooks";
import { ChartId } from "../ClusterDetail";
import MetricTypeSelector, { type MetricType } from "../Components/MetricTypeSelector";
import { Percentile } from "../Components/PercentileSelector";
import { TimeRange } from "../Components/TimeRangeSelector";
import WorkloadSelector from "../Components/WorkloadSelector";
import ChartCard from "./ChartCard";
import { formatCpu, formatMemory, formatTimestamp, formatTimestampAxis, getCommonChartOptions } from "./utils";

const chartStyles: React.CSSProperties = {
  height: "100%",
  width: "100%",
};

const xAxisIntervals = 10;

interface MultiWorkloadChartProps {
  chartRef?: React.RefObject<ECharts>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getEventHandlers?: (chartId: ChartId) => Record<string, (event: any) => void>;
}

const MultiWorkloadChart = ({ chartRef, getEventHandlers }: MultiWorkloadChartProps) => {
  const [selectedWorkloadNames, setSelectedWorkloadNames] = useState<string[]>([]);
  const [metricType, setMetricType] = useState<MetricType>("memory");

  const { workloads } = useWorkloadsMetrics({
    workloadNames: selectedWorkloadNames,
    clusterId: "123",
    timeRange: TimeRange.T_30D,
    percentile: Percentile.P_99,
  });

  const theme = useTheme();

  const metricWithLongestTimeRange = Object.values(workloads).reduce(
    (longest, current) => (current.length > longest.length ? current : longest),
    []
  );

  const baseMetrics = useMemo(
    () =>
      metricWithLongestTimeRange?.map((m) => ({
        ...m,
        formatted_start_time: formatTimestamp(m.start_time),
      })),
    [metricWithLongestTimeRange]
  );

  const chartOptions: EChartsOption = useMemo(
    () => ({
      ...getCommonChartOptions(theme, {
        yAxis: {
          axisLabel: {
            formatter: (value) => (metricType === "memory" ? formatMemory(value) : formatCpu(value, 1)),
          },
        },
        tooltip: {
          valueFormatter(value) {
            if (!value) return "0";

            if (metricType === "memory") {
              return formatMemory(String(value));
            } else {
              return formatCpu(String(value));
            }
          },
        },
      }),
      xAxis: {
        data: baseMetrics.map((m) => m.formatted_start_time),
        axisLabel: {
          formatter: formatTimestampAxis,
          interval: Math.floor(baseMetrics.length / xAxisIntervals),
        },
      },
      series: Object.entries(workloads).map(([name, data]) => ({
        name,
        type: "line",
        data: data.map((m) => m[metricType === "memory" ? "mem_use" : "cpu_use"] || 0),
        connectNulls: true,
        showSymbol: false,
      })),
      legend: {
        textStyle: {
          color: theme.palette.text.primary,
        },
        bottom: 0,
        type: "scroll",
        icon: "circle",
      },
    }),
    [workloads, baseMetrics, theme, metricType]
  );

  return (
    <Box sx={{ width: "100%" }}>
      <ChartCard
        title="Workload utilization"
        height={300}
        rightHeaderContent={
          <Stack direction="row" spacing={1}>
            <WorkloadSelector value={selectedWorkloadNames} onChange={setSelectedWorkloadNames} />
            <MetricTypeSelector value={metricType} onChange={setMetricType} />
          </Stack>
        }
      >
        {selectedWorkloadNames.length > 0 ? (
          <ECharts
            key={selectedWorkloadNames.length}
            style={chartStyles}
            option={chartOptions}
            ref={chartRef}
            onEvents={getEventHandlers?.(ChartId.WORKLOADS)}
            lazyUpdate
          />
        ) : (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "100%",
            }}
          >
            <Typography>Select workloads to see utilization</Typography>
          </Box>
        )}
      </ChartCard>
    </Box>
  );
};

export default MultiWorkloadChart;
